import './css/app.css';
import './css/main.css';
import './css/common.css';
import './css/payment.css';
import './css/mobile.css';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LoadingModal, MessageModal } from './component/modal/modal';
import { BRAND_LOGO, BRAND_NAME, COMPANY_NAME, HAS_CUSTOM_LOGO } from '../config/brand';

/** 카카오(다음) 우편번호 스크립트 로더 */
function loadDaumPostcode() {
    const SCRIPT_ID = 'daum-postcode-script';
    return new Promise((resolve, reject) => {
        if (window.daum?.Postcode) return resolve(true);
        if (document.getElementById(SCRIPT_ID)) {
            const check = setInterval(() => {
                if (window.daum?.Postcode) {
                    clearInterval(check);
                    resolve(true);
                }
            }, 50);
            setTimeout(() => {
                clearInterval(check);
                if (window.daum?.Postcode) resolve(true);
                else reject(new Error('Daum Postcode load timeout'));
            }, 5000);
            return;
        }
        const s = document.createElement('script');
        s.id = SCRIPT_ID;
        s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error('Daum Postcode script failed'));
        document.head.appendChild(s);
    });
}

function SmsAuthPaymentPublic() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ msg: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 복호화된 결제 데이터
    const [paymentData, setPaymentData] = useState({
        productName: "",
        amount: "",
        mid: "",
    });

    // URL 파라미터에서 암호화된 데이터 받기
    const encryptedData = searchParams.get("data") || "";

    // 폼 데이터
    const [formData, setFormData] = useState({
        BuyerName: "",
        BuyerHpTel: "",
        BuyerEmail: "",
        Postcode: "",
        BaseAddr: "",
        ExtraAddr: "",
        DetailAddr: "",
        BuyerAddr: "",
        // 카드 정보
        card_num: "",
        CardCvv: "",
        CardPw: "",
        ExpMonth: "",
        ExpYear: "",
        CardQuota: "00",
    });

    const [phoneDisplayValue, setPhoneDisplayValue] = useState("");
    const modalBodyRef = useRef(null);
    const detailInputRef = useRef(null);

    // 카드번호를 4개 input으로 분할해서 관리
    const [cardNum1, setCardNum1] = useState("");
    const [cardNum2, setCardNum2] = useState("");
    const [cardNum3, setCardNum3] = useState("");
    const [cardNum4, setCardNum4] = useState("");
    const cardNum2Ref = useRef(null);
    const cardNum3Ref = useRef(null);
    const cardNum4Ref = useRef(null);

    // ===== 주소검색 모달 관련 =====
    const [addrOpen, setAddrOpen] = useState(false);
    const addrLayerRef = useRef(null);

    useEffect(() => {
        if (addrOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [addrOpen]);

    const openAddressSearch = async () => {
        try {
            await loadDaumPostcode();
            setAddrOpen(true);
            setTimeout(() => {
                const bodyEl = modalBodyRef.current;
                const wrapEl = addrLayerRef.current;
                if (!bodyEl || !wrapEl) return;

                const applyHeight = () => {
                    wrapEl.style.height = `${bodyEl.clientHeight}px`;
                };
                applyHeight();

                wrapEl.innerHTML = "";
                new window.daum.Postcode({
                    oncomplete: (data) => {
                        let addr = '';
                        let extraAddr = '';

                        if (data.userSelectedType === 'R') {
                            addr = data.roadAddress;
                        } else {
                            addr = data.jibunAddress;
                        }

                        if (data.userSelectedType === 'R') {
                            if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) {
                                extraAddr += data.bname;
                            }
                            if (data.buildingName !== '' && data.apartment === 'Y') {
                                extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
                            }
                            if (extraAddr !== '') {
                                extraAddr = ' (' + extraAddr + ')';
                            }
                        }

                        setFormData(prev => ({
                            ...prev,
                            Postcode: data.zonecode,
                            BaseAddr: addr,
                            ExtraAddr: extraAddr
                        }));

                        if (detailInputRef.current) {
                            detailInputRef.current.focus();
                        }

                        setAddrOpen(false);
                    },
                    onresize: () => {
                        applyHeight();
                    },
                    width: '100%',
                    height: '100%',
                    maxSuggestItems: 5
                }).embed(wrapEl);
            }, 0);
        } catch (e) {
            console.error(e);
            setMessage({ msg: "주소 검색 스크립트 로딩에 실패했습니다." });
        }
    };

    // 암호화된 데이터 복호화
    useEffect(() => {
        if (!encryptedData) {
            setMessage({ msg: "결제 정보가 없습니다." });
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return;
        }

        // 백엔드 API로 복호화 요청
        const decryptData = async () => {
            try {
                const response = await axios.get(
                    process.env.REACT_APP_API + "/api/sms/decrypt",
                    {
                        params: { data: encryptedData },
                        withCredentials: true
                    }
                );

                if (response.data?.success && response.data?.data) {
                    const { productName, amount, mid } = response.data.data;
                    setPaymentData({
                        productName: productName || "",
                        amount: String(amount || ""),
                        mid: mid || "",
                    });
                    setLoading(false);
                } else {
                    setMessage({ msg: response.data?.msg || "결제 정보 복호화에 실패했습니다." });
                    setTimeout(() => {
                        navigate("/");
                    }, 2000);
                }
            } catch (error) {
                console.error("복호화 오류:", error);
                setMessage({ msg: "결제 정보를 불러오는 중 오류가 발생했습니다." });
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            }
        };

        decryptData();
    }, [encryptedData, navigate]);

    // 전화번호 입력 핸들러
    const handlePhoneChange = (e) => {
        let numeric = e.target.value.replace(/\D/g, "").slice(0, 11);
        const parts =
            numeric.length < 4
                ? [numeric]
                : numeric.length < 8
                    ? [numeric.slice(0, 3), numeric.slice(3)]
                    : [numeric.slice(0, 3), numeric.slice(3, 7), numeric.slice(7)];
        const displayVal = parts.join("-");
        setPhoneDisplayValue(displayVal);
        setFormData((prev) => ({ ...prev, BuyerHpTel: numeric }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // 카드번호 입력 핸들러 (4개 필드)
    const handleCardInput1 = (e) => {
        let val = e.target.value.replace(/\D/g, "");
        val = val.slice(0, 4);
        setCardNum1(val);
        if (val.length === 4) {
            cardNum2Ref.current?.focus();
        }
    };

    const handleCardInput2 = (e) => {
        let val = e.target.value.replace(/\D/g, "");
        val = val.slice(0, 4);
        setCardNum2(val);
        if (val.length === 4) {
            cardNum3Ref.current?.focus();
        }
    };

    const handleCardInput3 = (e) => {
        let val = e.target.value.replace(/\D/g, "");
        val = val.slice(0, 4);
        setCardNum3(val);
        if (val.length === 4) {
            cardNum4Ref.current?.focus();
        }
    };

    const handleCardInput4 = (e) => {
        let val = e.target.value.replace(/\D/g, "");
        val = val.slice(0, 4);
        setCardNum4(val);
    };

    // 카드번호 4개의 상태가 변할 때마다 formData.card_num에 합쳐서 저장
    useEffect(() => {
        const combined = cardNum1 + cardNum2 + cardNum3 + cardNum4;
        setFormData((prev) => ({
            ...prev,
            card_num: combined,
        }));
    }, [cardNum1, cardNum2, cardNum3, cardNum4]);

    // Base/Extra/Detail → BuyerAddr 자동 결합
    useEffect(() => {
        const combined = [formData.BaseAddr, formData.ExtraAddr, formData.DetailAddr]
            .filter(Boolean)
            .join(' ')
            .trim();
        if (combined !== formData.BuyerAddr) {
            setFormData((prev) => ({ ...prev, BuyerAddr: combined }));
        }
    }, [formData.BaseAddr, formData.ExtraAddr, formData.DetailAddr]);

    // 필수값 체크
    const requiredFilled =
        (formData.BuyerName || "").trim() !== "" &&
        (formData.BaseAddr || "").trim() !== "" &&
        (formData.BuyerEmail || "").trim() !== "" &&
        (formData.Postcode || "").trim() !== "" &&
        String(formData.BuyerHpTel || "").trim() !== "" &&
        formData.card_num.length === 16 &&
        formData.CardCvv.length === 3 &&
        formData.CardPw.length === 2 &&
        formData.ExpMonth.length === 2 &&
        formData.ExpYear.length === 2;

    const isDisabled = isSubmitting || !requiredFilled;

    // 금액 표시용 (천단위 콤마)
    const displayAmount = paymentData.amount ? parseInt(paymentData.amount, 10).toLocaleString("ko-KR") : "";

    // 결제 요청 (PASS인증 방식)
    const onSubmitPayment = async () => {
        if (isDisabled) return;
        setIsSubmitting(true);
        setLoading(true);

        try {
            const amt = parseInt(paymentData.amount, 10) || 0;
            const paymentId = `PAY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // 본인인증 시작 요청
            const authResponse = await axios.post(
                `${process.env.REACT_APP_API}/api/identity/start`,
                {
                    returnURL: `${process.env.REACT_APP_API}/api/identity/checkplus_success`,
                    payment_id: paymentId,
                    user_address: formData.BuyerAddr || "",
                    tel: formData.BuyerHpTel || "",
                    payment_data: {
                        mid: paymentData.mid || "",
                        amt: amt,
                        goods_name: paymentData.productName || "",
                        ord_nm: formData.BuyerName || "",
                        email: formData.BuyerEmail || "",
                        product_code: `SMS_AUTH_${Date.now()}`,
                        quantity: 1,
                        unit_price: amt,
                        tmn_id: "",
                        pay_method: "00",
                        cardNumber: formData.card_num || "",
                        cardExpiry: (formData.ExpYear || "") + (formData.ExpMonth || ""),
                        cardCvv: formData.CardCvv || "",
                        cardPw: formData.CardPw || "",
                        installment: formData.CardQuota || "00"
                    }
                },
                {
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );



            if (authResponse.data.success && authResponse.data.data) {
                const { req_no, token_version_id, enc_data, integrity, auth_url } = authResponse.data.data;

                if (req_no) {
                    localStorage.setItem('identity_req_no', req_no);
                    // 결제 정보 저장 (카드 민감정보 제외)
                    const { card_num, CardCvv, CardPw, IDNo, ...safeFormData } = formData;
                    localStorage.setItem('identity_payment_data', JSON.stringify({
                        formData: safeFormData,
                        productName: paymentData.productName,
                        amount: paymentData.amount
                    }));
                }

                if (!token_version_id || !enc_data || !integrity) {
                    setLoading(false);
                    setIsSubmitting(false);
                    setMessage({ msg: "본인인증 데이터가 올바르지 않습니다." });
                    return;
                }

                // NICE CheckPlus 본인인증 팝업 생성
                const popupWindow = window.open(
                    '',
                    'popupChk',
                    'width=480, height=812, top=100, fullscreen=no, menubar=no, status=no, titlebar=yes, location=no, toolbar=no, scrollbar=no'
                );

                if (!popupWindow) {
                    alert("팝업이 차단되었습니다. 팝업 차단을 해제해주세요.");
                    setLoading(false);
                    setIsSubmitting(false);
                    return;
                }

                // NICE CheckPlus 본인인증 폼 생성
                const form = document.createElement('form');
                form.name = 'form_chk';
                form.method = 'post';
                form.action = process.env.REACT_APP_NICE_AUTH_URL;
                form.target = 'popupChk';
                form.style.display = 'none';

                const formFields = {
                    'm': 'service',
                    'token_version_id': token_version_id,
                    'enc_data': enc_data,
                    'integrity_value': integrity
                };

                Object.keys(formFields).forEach(key => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                });

                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);

                // 팝업 닫힘 감지 및 결과 처리
                const checkClosed = setInterval(() => {
                    if (popupWindow.closed) {
                        clearInterval(checkClosed);
                        if (req_no) {
                            setLoading(true);
                            pollAuthResult(req_no);
                        }
                    }
                }, 1000);

                setTimeout(() => {
                    if (!popupWindow.closed) {
                        popupWindow.close();
                        clearInterval(checkClosed);
                        setLoading(false);
                        setIsSubmitting(false);
                        setMessage({ msg: "PASS인증 시간이 초과되었습니다." });
                        localStorage.removeItem('identity_req_no');
                        localStorage.removeItem('identity_payment_data');
                    }
                }, 300000);
            } else {
                setLoading(false);
                setIsSubmitting(false);
                setMessage({ msg: authResponse.data.message || "본인인증 데이터 생성에 실패했습니다." });
            }
        } catch (error) {
            console.error("PASS인증 요청 오류:", error);
            setLoading(false);
            setIsSubmitting(false);
            setMessage({ msg: "PASS인증 요청 중 오류가 발생했습니다." });
        }
    };

    // PASS인증 결과 조회 (폴링)
    const pollAuthResult = async (requestNo) => {
        if (!requestNo) {
            setLoading(false);
            setIsSubmitting(false);
            return;
        }

        const maxAttempts = 60;
        let attempts = 0;
        const pollInterval = 2000;

        const poll = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API}/api/identity/result?req_no=${requestNo}`
                );

                const result = response.data;


                if (result.success && result.status === 'completed') {
                    localStorage.removeItem('identity_req_no');
                    localStorage.removeItem('identity_payment_data');

                    setLoading(false);
                    setIsSubmitting(false);

                    navigate("/urlComplete", {
                        state: {
                            receipt: {
                                success: true,
                                paymentType: 'pass',
                                authData: result.data
                            },
                            listData: { product_name: paymentData.productName, price: paymentData.amount },
                        },
                        replace: false,
                    });
                    return;
                } else if (result.status === 'pending') {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, pollInterval);
                    } else {
                        setLoading(false);
                        setIsSubmitting(false);
                        setMessage({ msg: "인증 결과 조회 시간이 초과되었습니다." });
                        localStorage.removeItem('identity_req_no');
                        localStorage.removeItem('identity_payment_data');
                    }
                } else {
                    setLoading(false);
                    setIsSubmitting(false);
                    setMessage({ msg: result.message || "본인인증에 실패했습니다." });
                    localStorage.removeItem('identity_req_no');
                    localStorage.removeItem('identity_payment_data');
                }
            } catch (error) {
                console.error("인증 결과 조회 오류:", error);
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, pollInterval);
                } else {
                    setLoading(false);
                    setIsSubmitting(false);
                    setMessage({ msg: "인증 결과 조회 중 오류가 발생했습니다." });
                    localStorage.removeItem('identity_req_no');
                    localStorage.removeItem('identity_payment_data');
                }
            }
        };

        poll();
    };

    return (
        <>
            {loading ? <LoadingModal /> : null}
            <MessageModal message={message} />

            <div style={{ 
                minHeight: '100vh', 
                background: "#f5f6f8",
                padding: '0 0 100px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}>
                {/* 상단 헤더 */}
                <header style={{
                    background: '#fff',
                    borderBottom: '1px solid #e5e8eb',
                    padding: '16px 20px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {HAS_CUSTOM_LOGO ? (
                                <img src={BRAND_LOGO} alt={`${BRAND_NAME} 로고`} style={{ width: '96px', maxHeight: '44px', objectFit: 'contain' }} />
                            ) : (
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: '#03c75a',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontWeight: '700',
                                    fontSize: '18px'
                                }}>
                                    P
                                </div>
                            )}
                            <div>
                                <h1 style={{ 
                                    fontSize: '18px', 
                                    fontWeight: '700', 
                                    color: '#000',
                                    margin: 0,
                                    lineHeight: '1.2'
                                }}>
                                    {BRAND_NAME}
                                </h1>
                                <p style={{ 
                                    fontSize: '12px', 
                                    color: '#666', 
                                    margin: 0,
                                    lineHeight: '1.2'
                                }}>
                                    안전한 결제 서비스
                                </p>
                            </div>
                        </div>
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            padding: '6px 12px',
                            background: '#e8f5e9',
                            borderRadius: '12px',
                            color: '#2e7d32',
                            fontWeight: '500'
                        }}>
                            PASS인증
                        </div>
                    </div>
                </header>

                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
                    {/* 페이지 제목 */}
                    <div style={{ 
                        marginBottom: '20px'
                    }}>
                        <h2 style={{ 
                            fontSize: '24px', 
                            fontWeight: '700', 
                            color: '#000',
                            margin: '0 0 8px 0'
                        }}>
                            PASS인증 결제
                        </h2>
                        <p style={{ 
                            fontSize: '14px', 
                            color: '#666', 
                            margin: 0
                        }}>
                            본인인증을 통해 안전하게 결제를 진행해주세요
                        </p>
                    </div>

                    {/* 주문상품 */}
                    <div style={{
                        background: '#fff',
                        padding: '20px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#000',
                            margin: '0 0 16px 0',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #e5e8eb'
                        }}>
                            주문상품
                        </h2>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ 
                                fontSize: '16px', 
                                color: '#000',
                                marginBottom: '8px',
                                fontWeight: '500'
                            }}>
                                {paymentData.productName || "상품명"}
                            </div>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '16px',
                            borderTop: '1px solid #e5e8eb'
                        }}>
                            <span style={{ fontSize: '16px', color: '#666', fontWeight: '500' }}>총 주문금액</span>
                            <span style={{ fontSize: '24px', color: '#000', fontWeight: '700' }}>
                                {displayAmount || "0"}원
                            </span>
                        </div>
                    </div>

                    {/* 배송지 정보 */}
                    <div style={{
                        background: '#fff',
                        padding: '20px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#000',
                            margin: '0 0 20px 0',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #e5e8eb'
                        }}>
                            배송지 정보
                        </h2>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                구매자명 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <input
                                name="BuyerName"
                                placeholder="이름을 입력하세요"
                                value={formData.BuyerName}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                연락처 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <input
                                name="BuyerHpTel_display"
                                placeholder="010-0000-0000"
                                value={phoneDisplayValue}
                                onChange={handlePhoneChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                이메일 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <input
                                name="BuyerEmail"
                                type="email"
                                placeholder="email@example.com"
                                value={formData.BuyerEmail}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                주소 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input
                                    name="Postcode"
                                    placeholder="우편번호"
                                    value={formData.Postcode}
                                    onChange={handleChange}
                                    readOnly
                                    style={{
                                        width: '120px',
                                        padding: '12px 16px',
                                        fontSize: '16px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        background: '#f9fafb',
                                        outline: 'none'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={openAddressSearch}
                                    style={{
                                        padding: '12px 20px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        border: '1px solid #03c75a',
                                        borderRadius: '6px',
                                        background: '#fff',
                                        color: '#03c75a',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.background = '#03c75a';
                                        e.target.style.color = '#fff';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.background = '#fff';
                                        e.target.style.color = '#03c75a';
                                    }}
                                >
                                    주소 검색
                                </button>
                            </div>
                            <input
                                name="BaseAddr"
                                placeholder="도로명/지번 주소"
                                value={formData.BaseAddr + (formData.ExtraAddr ? ` ${formData.ExtraAddr}` : '')}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, BaseAddr: e.target.value, ExtraAddr: "" }));
                                }}
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    marginBottom: '8px',
                                    background: '#f9fafb',
                                    outline: 'none'
                                }}
                            />
                            <input
                                ref={detailInputRef}
                                name="DetailAddr"
                                placeholder="상세주소를 입력하세요"
                                value={formData.DetailAddr}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>
                    </div>

                    {/* 카드 정보 */}
                    <div style={{
                        background: '#fff',
                        padding: '20px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#000',
                            margin: '0 0 20px 0',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #e5e8eb'
                        }}>
                            카드 정보
                        </h2>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                카드번호 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <div style={{
                                display: "flex",
                                gap: "8px",
                                width: '100%',
                                boxSizing: 'border-box'
                            }}>
                                <input
                                    type="text"
                                    placeholder="0000"
                                    maxLength={4}
                                    value={cardNum1}
                                    onChange={handleCardInput1}
                                    style={{
                                        flex: "1",
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <input
                                    ref={cardNum2Ref}
                                    type="text"
                                    placeholder="0000"
                                    maxLength={4}
                                    value={cardNum2}
                                    onChange={handleCardInput2}
                                    style={{
                                        flex: "1",
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <input
                                    ref={cardNum3Ref}
                                    type="text"
                                    placeholder="0000"
                                    maxLength={4}
                                    value={cardNum3}
                                    onChange={handleCardInput3}
                                    style={{
                                        flex: "1",
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <input
                                    ref={cardNum4Ref}
                                    type="text"
                                    placeholder="0000"
                                    maxLength={4}
                                    value={cardNum4}
                                    onChange={handleCardInput4}
                                    style={{
                                        flex: "1",
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                유효기간 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center", width: '100%', boxSizing: 'border-box' }}>
                                <input
                                    type="text"
                                    name="ExpYear"
                                    placeholder="YY"
                                    maxLength={2}
                                    value={formData.ExpYear}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "").slice(0, 2);
                                        setFormData((prev) => ({ ...prev, ExpYear: value }));
                                        if (value.length === 2) {
                                            document.querySelector('input[name="ExpMonth"]')?.focus();
                                        }
                                    }}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <span style={{ fontSize: "18px", color: "#666", flexShrink: 0 }}>/</span>
                                <input
                                    type="text"
                                    name="ExpMonth"
                                    placeholder="MM"
                                    maxLength={2}
                                    value={formData.ExpMonth}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, "");
                                        if (value.length === 2) {
                                            const month = parseInt(value);
                                            if (month < 1 || month > 12) {
                                                value = value.slice(0, 1);
                                            }
                                        }
                                        setFormData((prev) => ({ ...prev, ExpMonth: value.slice(0, 2) }));
                                    }}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px",
                                        textAlign: "center",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                CVV / 비밀번호 <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <div style={{ display: "flex", gap: "8px", width: '100%', boxSizing: 'border-box' }}>
                                <input
                                    name="CardCvv"
                                    type="password"
                                    placeholder="카드 뒷면 3자리"
                                    maxLength={3}
                                    value={formData.CardCvv}
                                    onChange={handleChange}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px 16px",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                                <input
                                    name="CardPw"
                                    type="password"
                                    placeholder="앞 2자리"
                                    maxLength={2}
                                    value={formData.CardPw}
                                    onChange={handleChange}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: "16px",
                                        padding: "12px 16px",
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                marginBottom: '8px'
                            }}>
                                할부 개월
                            </label>
                            <select
                                name="CardQuota"
                                value={formData.CardQuota}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#03c75a'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            >
                                <option value="00">일시불</option>
                                <option value="02">2개월</option>
                                <option value="03">3개월</option>
                                <option value="04">4개월</option>
                                <option value="05">5개월</option>
                            </select>
                        </div>

                        {/* PASS인증 안내 */}
                        <div style={{
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4caf50',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginTop: '20px',
                            fontSize: '14px',
                            color: '#2e7d32'
                        }}>
                            <strong>PASS인증 결제</strong><br />
                            본인인증을 통해 안전하게 결제하실 수 있습니다.
                            </div>
                        </div>
                    </div>
                </div>

                {/* 하단 푸터 */}
                <footer style={{
                    background: '#fff',
                    borderTop: '1px solid #e5e8eb',
                    padding: '24px 20px',
                    marginTop: '40px'
                }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            fontSize: '12px',
                            color: '#666'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap'
                            }}>
                                <a href="#" style={{ color: '#666', textDecoration: 'none' }}>이용약관</a>
                                <a href="#" style={{ color: '#666', textDecoration: 'none', fontWeight: '500' }}>개인정보처리방침</a>
                                <a href="#" style={{ color: '#666', textDecoration: 'none' }}>고객센터</a>
                            </div>
                            <div style={{
                                paddingTop: '16px',
                                borderTop: '1px solid #e5e8eb',
                                lineHeight: '1.6'
                            }}>
                                <div style={{ marginBottom: '4px' }}>
                                    <strong style={{ color: '#333' }}>{COMPANY_NAME}</strong>
                                </div>
                                <div style={{ color: '#999' }}>
                                    대표이사: 홍길동 | 사업자등록번호: 123-45-67890<br/>
                                    주소: 서울특별시 강남구 테헤란로 123<br/>
                                    고객센터: 1588-0000 | 이메일: help@payblue.com
                                </div>
                                <div style={{ marginTop: '12px', color: '#999' }}>
                                    Copyright © {BRAND_NAME}. All rights reserved.
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>

                {/* 하단 고정 버튼 */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: '#fff',
                padding: '16px 20px',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxWidth: '600px',
                margin: '0 auto'
            }}>
                <button
                    onClick={onSubmitPayment}
                    disabled={isDisabled}
                    style={{
                        width: '100%',
                        padding: '16px',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#fff',
                        background: isDisabled ? '#d1d5db' : '#03c75a',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => {
                        if (!isDisabled) {
                            e.target.style.background = '#02b350';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!isDisabled) {
                            e.target.style.background = '#03c75a';
                        }
                    }}
                >
                    {isSubmitting ? "결제중..." : `${displayAmount || "0"}원 PASS인증 결제하기`}
                </button>
            </div>

            {/* 주소검색 모달 */}
            {addrOpen && (
                <div
                    onClick={() => setAddrOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: 16,
                        overflow: 'hidden',
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 'min(720px, 100%)',
                            maxHeight: '90vh',
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 12px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff', zIndex: 1
                        }}>
                            <b>주소 검색</b>
                            <button
                                onClick={() => setAddrOpen(false)}
                                style={{ border: 'none', background: '#000', color: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
                            >
                                닫기
                            </button>
                        </div>

                        <div
                            ref={modalBodyRef}
                            style={{
                                flex: '1 1 auto',
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                overscrollBehavior: 'contain',
                            }}
                        >
                            <div ref={addrLayerRef} style={{ width: '100%', height: '100%', minHeight: 420 }} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SmsAuthPaymentPublic;
